import React, { useState, useEffect, useCallback } from 'react';
import barcodePrintService from '../../services/barcodePrintService';
import thermalAgentService from '../../services/thermalAgentService';
import ThermalSpecimenLabel from './ThermalSpecimenLabel';
import './ThermalBarcodePrintModal.css';

/**
 * Phlebotomist & Technician Specimen Tube Label Printing Modal
 * Strict Zero-Configuration Architecture:
 * - Automatically resolves active template, dimensions, barcode symbology, and mapped printer.
 * - Supports direct Thermal hardware dispatch (ZPL/TSPL) via Local Print Agent (port 19826).
 * - Full Virtual Hardware Simulation mode for testing without a physical barcode printer.
 * - Raw ZPL / TSPL Code Inspector & Spool Queue Monitor.
 */

const REPRINT_REASONS = [
  'Damaged Label / Smudged Barcode',
  'Printer Jam / Mechanical Error',
  'Label Lost / Misplaced in Transport',
  'Specimen Recollection / Re-draw',
  'Wrong Tube / Container Transferred',
  'Other Reason (specify)'
];

export const ThermalBarcodePrintModal = ({
  isOpen,
  onClose,
  sample,
  onPrintSuccess = null
}) => {
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(true);
  const [resolvedConfig, setResolvedConfig] = useState(null);
  const [error, setError] = useState(null);

  // Agent connectivity state
  const [agentHealth, setAgentHealth] = useState({ isOnline: false, checking: true, printers: [], version: null });

  // Tab View: 'PREVIEW' | 'ZPL' | 'SPOOL'
  const [activeTab, setActiveTab] = useState('PREVIEW');
  const [rawZpl, setRawZpl] = useState('');
  const [spoolJobs, setSpoolJobs] = useState([]);
  const [copiedZpl, setCopiedZpl] = useState(false);

  // Duplicate Check & Reprint State
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [reprintReason, setReprintReason] = useState(REPRINT_REASONS[0]);
  const [customReason, setCustomReason] = useState('');
  const [previousPrintInfo, setPreviousPrintInfo] = useState(null);

  // Copies & Zoom
  const [copies, setCopies] = useState(1);
  const [zoom, setZoom] = useState(1.25);
  const [printStatus, setPrintStatus] = useState(null);

  // Check Local Agent Health
  const checkAgentHealth = useCallback(async () => {
    setAgentHealth((prev) => ({ ...prev, checking: true }));
    const health = (await thermalAgentService.checkAgentHealth()) || {};
    setAgentHealth({
      isOnline: Boolean(health.isOnline),
      checking: false,
      printers: health.printers || [],
      version: health.version || null
    });
  }, []);

  // Fetch recent spool jobs
  const loadSpoolJobs = useCallback(async () => {
    const jobs = await thermalAgentService.getRecentJobs();
    setSpoolJobs(Array.isArray(jobs) ? jobs : []);
  }, []);

  // Resolve config on modal open
  const resolveConfig = useCallback(async () => {
    if (!sample) return;
    setResolving(true);
    setError(null);
    setPrintStatus(null);
    try {
      const res = await barcodePrintService.resolvePrintConfig({
        centreId: sample.centreId || sample.centreID,
        departmentId: sample.departmentId || sample.subCategoryID,
        departmentName: sample.departmentName,
        sampleTypeId: sample.sampleTypeId,
        sampleTypeName: sample.sampleTypeName,
        containerTypeId: sample.containerTypeId,
        containerName: sample.containerName,
        testId: sample.testId,
        barcodeNo: sample.barcodeNo
      });

      if (res.success && res.data) {
        setResolvedConfig(res.data);
        if (res.data.isAlreadyPrinted) {
          setIsDuplicate(true);
          setPreviousPrintInfo({
            count: res.data.previousPrintCount,
            lastAt: res.data.lastPrintedAt,
            lastBy: res.data.lastPrintedBy
          });
        } else {
          setIsDuplicate(false);
        }

        // Prefetch Raw Thermal ZPL Payload for live code inspector
        try {
          const payloadRes = await barcodePrintService.generateThermalPayload({
            templateId: res.data.template?.templateId,
            printerId: res.data.primaryPrinter?.printerId,
            protocol: res.data.primaryPrinter?.protocol || 'ZPL',
            sampleData: {
              patientName: sample.patientName || 'PATIENT',
              uhid: sample.uhid || sample.patientId || '',
              patientId: sample.patientId || '',
              labNo: sample.ledgerTransactionNo || sample.labNo || '',
              ledgerTransactionNo: sample.ledgerTransactionNo || sample.labNo || '',
              barcodeNo: sample.barcodeNo || (sample.testId ? `S-${sample.testId}` : '10000001'),
              barcode: sample.barcodeNo || (sample.testId ? `S-${sample.testId}` : '10000001'),
              itemName: sample.itemName || sample.testName || 'Investigation',
              testName: sample.itemName || sample.testName || 'Investigation',
              sampleTypeName: sample.sampleTypeName || 'Specimen',
              containerName: sample.containerName || 'Standard',
              departmentName: sample.departmentName || 'Pathology',
              centreName: sample.centreName || 'AURO DIAGNOSTICS',
              ageGender: sample.ageGender || `${sample.age || ''} ${sample.gender || ''}`.trim()
            },
            copies: 1
          });
          if (payloadRes?.success && payloadRes?.data?.rawPayload) {
            setRawZpl(payloadRes.data.rawPayload);
          }
        } catch (e) {
          console.warn('Could not prefetch raw ZPL payload:', e);
        }
      } else {
        setError(res.message || 'No active Barcode Label Template found for this specimen.');
      }
    } catch (err) {
      console.error('Failed to resolve label configuration:', err);
      setError('Error resolving laboratory print configuration.');
    } finally {
      setResolving(false);
    }
  }, [sample]);

  useEffect(() => {
    if (isOpen && sample) {
      resolveConfig();
      checkAgentHealth();
      loadSpoolJobs();
    }
  }, [isOpen, sample, resolveConfig, checkAgentHealth, loadSpoolJobs]);

  if (!isOpen || !sample) return null;

  // Execute Direct Thermal Print
  const handlePrint = async () => {
    setLoading(true);
    setPrintStatus(null);
    try {
      const finalReprintReason = isDuplicate
        ? (reprintReason === 'Other Reason (specify)' ? customReason.trim() || 'Other' : reprintReason)
        : null;

      const result = await barcodePrintService.executePrintWorkflow({
        sample,
        isReprint: isDuplicate,
        reprintReason: finalReprintReason,
        copies
      });

      if (result.success) {
        setPrintStatus({
          type: 'success',
          message: `✓ Dispatched ${copies} label(s) to ${result.printer?.printerName || 'Thermal Printer'} via Local Print Agent. Zero browser dialogs.`
        });
        loadSpoolJobs();
        if (onPrintSuccess) onPrintSuccess(sample);
        setTimeout(() => {
          onClose();
        }, 1800);
      } else {
        if (result.requiresFallbackVectorPrint) {
          setPrintStatus({
            type: 'warning',
            message: '⚠️ Local Thermal Agent is offline. You can test in Virtual Mode below or click Browser Print.'
          });
        } else {
          setPrintStatus({
            type: 'error',
            message: result.error || 'Thermal printing failed.'
          });
        }
      }
    } catch (err) {
      console.error('Print execution error:', err);
      setPrintStatus({
        type: 'error',
        message: err.message || 'Printing error occurred.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Execute Virtual Simulation (Zero physical hardware required)
  const handleVirtualSimulation = async () => {
    setLoading(true);
    setPrintStatus(null);
    try {
      const finalReprintReason = isDuplicate
        ? (reprintReason === 'Other Reason (specify)' ? customReason.trim() || 'Other' : reprintReason)
        : null;

      // 1. Dispatch to Virtual Spooler on Local Agent
      const agentRes = await thermalAgentService.sendRawThermalJob({
        printerName: 'Virtual Zebra ZD220 (50x25mm 203DPI)',
        protocol: 'ZPL',
        rawPayload: rawZpl || `^XA^PW400^LL200^FO20,20^A0N,24,20^FD${sampleData.patientName}^FS^FO20,60^BY2,2.5,50^BCN,50,Y,N,N^FD${sampleData.barcodeNo}^FS^XZ`,
        copies,
        jobTitle: `Virtual Simulation - ${sampleData.barcodeNo}`
      });

      // 2. Audit log in database
      try {
        await barcodePrintService.logPrintJob({
          testId: sample.testId || null,
          ledgerTransactionNo: sampleData.ledgerTransactionNo,
          barcodeNo: sampleData.barcodeNo,
          patientId: sampleData.patientId,
          patientName: sampleData.patientName,
          templateId: template?.templateId || 1,
          templateVersion: template?.version || 1,
          printerId: primaryPrinter?.printerId || 1,
          protocol: 'ZPL',
          status: 'SIMULATED',
          attemptCount: 1,
          printedPayload: rawZpl,
          isReprint: isDuplicate,
          reprintReason: finalReprintReason
        });
      } catch (logErr) {
        console.warn('Virtual audit logging notice:', logErr);
      }

      setPrintStatus({
        type: 'success',
        message: `✓ Virtual Print Successful! ${copies} label(s) recorded in local spool (${agentRes.jobId || 'VIRTUAL'}). Database audit trail updated.`
      });
      loadSpoolJobs();
      if (onPrintSuccess) onPrintSuccess(sample);
    } catch (err) {
      setPrintStatus({
        type: 'error',
        message: 'Virtual simulation error: ' + err.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Browser Vector Print (Only when explicitly triggered)
  const handleBrowserPrint = () => {
    window.print();
    if (onPrintSuccess) onPrintSuccess(sample);
  };

  const handleCopyZpl = () => {
    if (!rawZpl) return;
    navigator.clipboard.writeText(rawZpl).then(() => {
      setCopiedZpl(true);
      setTimeout(() => setCopiedZpl(false), 2000);
    });
  };

  const template = resolvedConfig?.template;
  const primaryPrinter = resolvedConfig?.primaryPrinter;

  const sampleData = {
    patientName: sample.patientName || 'PATIENT',
    uhid: sample.uhid || sample.patientId || '',
    patientId: sample.patientId || '',
    labNo: sample.ledgerTransactionNo || sample.labNo || '',
    ledgerTransactionNo: sample.ledgerTransactionNo || sample.labNo || '',
    barcodeNo: sample.barcodeNo || (sample.testId ? `S-${sample.testId}` : '10000001'),
    barcode: sample.barcodeNo || (sample.testId ? `S-${sample.testId}` : '10000001'),
    itemName: sample.itemName || sample.testName || 'Investigation',
    testName: sample.itemName || sample.testName || 'Investigation',
    sampleTypeName: sample.sampleTypeName || 'Specimen',
    containerName: sample.containerName || 'Standard',
    departmentName: sample.departmentName || 'Pathology',
    centreName: sample.centreName || 'AURO DIAGNOSTICS',
    ageGender: sample.ageGender || `${sample.age || ''} ${sample.gender || ''}`.trim()
  };

  return (
    <div className="tbpm-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="tbpm-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tbpm-header">
          <div className="tbpm-title-group">
            <span className="tbpm-icon">🏷️</span>
            <div>
              <h3 className="tbpm-title">Specimen Tube Label Print</h3>
              <span className="tbpm-subtitle">
                Master-Driven Thermal Engine • Zero-Config Phlebotomy & Technician Dispatch
              </span>
            </div>
          </div>
          <button type="button" className="tbpm-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Modal Body */}
        <div className="tbpm-body">
          {resolving ? (
            <div className="tbpm-resolving-box">
              <span className="tbpm-spinner">⏳</span>
              <p>Resolving active template, printer mapping, and checking duplicate print audit...</p>
            </div>
          ) : error ? (
            <div className="tbpm-error-banner">
              <span>⚠️</span>
              <div>
                <strong>Configuration Notice</strong>
                <p>{error}</p>
              </div>
            </div>
          ) : (
            <>
              {/* Agent Health Status Strip */}
              <div className={`tbpm-agent-bar ${agentHealth.isOnline ? 'agent-online' : 'agent-offline'}`}>
                <div className="tbpm-agent-status">
                  <span className={`tbpm-agent-dot ${agentHealth.isOnline ? 'dot-green' : 'dot-amber'}`} />
                  <strong>
                    {agentHealth.isOnline
                      ? 'Local Thermal Print Agent: Online (Port 19826)'
                      : 'Local Thermal Print Agent: Offline'}
                  </strong>
                  <span className="tbpm-agent-hint">
                    {agentHealth.isOnline
                      ? '• Direct raw thermal spooling active (Virtual + Physical hardware)'
                      : '• Start start-agent.bat for direct silent thermal printing'}
                  </span>
                </div>
                <button
                  type="button"
                  className="tbpm-btn-agent-check"
                  onClick={checkAgentHealth}
                  title="Refresh Local Agent status"
                >
                  🔄 Check
                </button>
              </div>

              {/* Duplicate Print Guard Alert */}
              {isDuplicate && (
                <div className="tbpm-reprint-banner">
                  <div className="tbpm-reprint-header">
                    <span>⚠️</span>
                    <strong>Duplicate Barcode Print Detected</strong>
                  </div>
                  <p>
                    This tube barcode (<strong>{sampleData.barcodeNo}</strong>) has already been printed{' '}
                    <strong>{previousPrintInfo?.count || 1} time(s)</strong>
                    {previousPrintInfo?.lastAt && ` (Last printed on ${new Date(previousPrintInfo.lastAt).toLocaleString()})`}.
                  </p>
                  <div className="tbpm-reprint-reason-box">
                    <label className="tbpm-label">
                      Select Mandatory Reprint Reason <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <select
                      className="tbpm-select"
                      value={reprintReason}
                      onChange={(e) => setReprintReason(e.target.value)}
                    >
                      {REPRINT_REASONS.map((r, i) => (
                        <option key={i} value={r}>{r}</option>
                      ))}
                    </select>
                    {reprintReason === 'Other Reason (specify)' && (
                      <input
                        type="text"
                        className="tbpm-input"
                        placeholder="Please specify why reprint is needed..."
                        value={customReason}
                        onChange={(e) => setCustomReason(e.target.value)}
                        maxLength={150}
                        style={{ marginTop: '6px' }}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Resolved Configuration Badge Banner */}
              <div className="tbpm-info-grid">
                <div className="tbpm-info-pill">
                  <span className="tbpm-pill-lbl">Template</span>
                  <strong className="tbpm-pill-val">{template?.templateName || 'Standard Specimen Tube'}</strong>
                  <span className="tbpm-pill-sub">{template?.labelWidthMm || 50} × {template?.labelHeightMm || 25} mm ({template?.dpi || 203} DPI)</span>
                </div>
                <div className="tbpm-info-pill">
                  <span className="tbpm-pill-lbl">Target Thermal Printer</span>
                  <strong className="tbpm-pill-val">
                    {agentHealth.isOnline
                      ? 'Virtual Zebra ZD220 (50x25mm)'
                      : (primaryPrinter?.printerName || 'Zebra ZD220')}
                  </strong>
                  <span className="tbpm-pill-sub">Protocol: {primaryPrinter?.protocol || 'ZPL'} • Connection: {agentHealth.isOnline ? 'LOCAL_AGENT (19826)' : 'OFFLINE'}</span>
                </div>
                <div className="tbpm-info-pill">
                  <span className="tbpm-pill-lbl">Barcode Identifier</span>
                  <strong className="tbpm-pill-val font-mono">{sampleData.barcodeNo}</strong>
                  <span className="tbpm-pill-sub">Symbology: {template?.symbology || 'CODE128'}</span>
                </div>
              </div>

              {/* Interactive Inspector Tabs */}
              <div className="tbpm-tabs">
                <button
                  type="button"
                  className={`tbpm-tab-btn ${activeTab === 'PREVIEW' ? 'active' : ''}`}
                  onClick={() => setActiveTab('PREVIEW')}
                >
                  🏷️ Live Label Preview
                </button>
                <button
                  type="button"
                  className={`tbpm-tab-btn ${activeTab === 'ZPL' ? 'active' : ''}`}
                  onClick={() => setActiveTab('ZPL')}
                >
                  💻 Raw Thermal ZPL Code
                </button>
                <button
                  type="button"
                  className={`tbpm-tab-btn ${activeTab === 'SPOOL' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('SPOOL');
                    loadSpoolJobs();
                  }}
                >
                  📜 Virtual Spool History ({spoolJobs.length})
                </button>
              </div>

              {/* TAB 1: Live Visual Preview */}
              {activeTab === 'PREVIEW' && (
                <div className="tbpm-preview-container">
                  <div className="tbpm-preview-toolbar">
                    <span className="tbpm-preview-label">Live 50×25mm Thermal Label Simulation</span>
                    <div className="tbpm-zoom-controls">
                      <button type="button" onClick={() => setZoom(1.0)} className={zoom === 1.0 ? 'active' : ''}>100%</button>
                      <button type="button" onClick={() => setZoom(1.25)} className={zoom === 1.25 ? 'active' : ''}>125%</button>
                      <button type="button" onClick={() => setZoom(1.5)} className={zoom === 1.5 ? 'active' : ''}>150%</button>
                    </div>
                  </div>

                  <div className="tbpm-preview-stage">
                    <ThermalSpecimenLabel
                      template={template}
                      sampleData={sampleData}
                      zoom={zoom}
                    />
                  </div>
                </div>
              )}

              {/* TAB 2: Raw ZPL Code Inspector */}
              {activeTab === 'ZPL' && (
                <div className="tbpm-zpl-box">
                  <div className="tbpm-zpl-header">
                    <div>
                      <strong>Raw Thermal Command Stream (ZPL II)</strong>
                      <span className="tbpm-zpl-badge">Zebra ZD220 • 203 DPI • 50×25 mm</span>
                    </div>
                    <button
                      type="button"
                      className="tbpm-btn-copy"
                      onClick={handleCopyZpl}
                    >
                      {copiedZpl ? '✓ Copied!' : '📋 Copy ZPL'}
                    </button>
                  </div>
                  <pre className="tbpm-zpl-code font-mono">
                    {rawZpl || (
                      `^XA\n^PW400\n^LL200\n^FO20,15^A0N,22,18^FD${sampleData.patientName}^FS\n^FO20,45^A0N,16,14^FDUHID: ${sampleData.uhid}   Lab: ${sampleData.ledgerTransactionNo}^FS\n^FO20,80^BY2,2.5,50^BCN,50,Y,N,N^FD${sampleData.barcodeNo}^FS\n^FO20,155^A0N,16,14^FD${sampleData.itemName}   ${sampleData.sampleTypeName}^FS\n^XZ`
                    )}
                  </pre>
                  <p className="tbpm-zpl-hint">
                    💡 This exact command stream is dispatched silently to Zebra/TSC thermal printheads via port 19826.
                    No PDF dialogs or print spool popups are triggered in direct thermal mode.
                  </p>
                </div>
              )}

              {/* TAB 3: Virtual Spool Queue History */}
              {activeTab === 'SPOOL' && (
                <div className="tbpm-spool-box">
                  <div className="tbpm-spool-header">
                    <strong>Recent Virtual Print Spool Jobs</strong>
                    <button type="button" className="tbpm-btn-agent-check" onClick={loadSpoolJobs}>
                      🔄 Refresh Spool
                    </button>
                  </div>
                  {spoolJobs.length === 0 ? (
                    <div className="tbpm-spool-empty">
                      <span>📭</span>
                      <p>No print jobs recorded in local spool yet. Click &quot;Simulate Virtual Print&quot; to test without hardware!</p>
                    </div>
                  ) : (
                    <div className="tbpm-spool-list">
                      {spoolJobs.map((j) => (
                        <div key={j.jobId} className="tbpm-spool-item">
                          <div className="tbpm-spool-item-top">
                            <span className="tbpm-spool-id font-mono">{j.jobId}</span>
                            <span className="tbpm-spool-time font-mono">
                              {new Date(j.timestamp).toLocaleTimeString()}
                            </span>
                            <span className="tbpm-spool-badge">{j.isVirtual ? 'VIRTUAL' : 'PHYSICAL'}</span>
                          </div>
                          <div className="tbpm-spool-item-desc">
                            <strong>{j.jobTitle}</strong> • {j.printerName} • {j.copies} copy(s) • {j.payloadSize} bytes
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Print Status Feedback */}
              {printStatus && (
                <div className={`tbpm-status-banner status-${printStatus.type}`}>
                  <span>{printStatus.type === 'success' ? '✓' : '⚠️'}</span>
                  <span>{printStatus.message}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="tbpm-footer">
          <div className="tbpm-copies-control">
            <label className="tbpm-copies-label">Copies:</label>
            <div className="tbpm-qty-box">
              <button
                type="button"
                className="tbpm-qty-btn"
                onClick={() => setCopies((c) => Math.max(1, c - 1))}
                disabled={copies <= 1 || loading}
              >
                -
              </button>
              <span className="tbpm-qty-val font-mono">{copies}</span>
              <button
                type="button"
                className="tbpm-qty-btn"
                onClick={() => setCopies((c) => Math.min(10, c + 1))}
                disabled={copies >= 10 || loading}
              >
                +
              </button>
            </div>
          </div>

          <div className="tbpm-footer-actions">
            <button
              type="button"
              className="tbpm-btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>

            {/* Test Virtually Button (Available even with zero physical hardware) */}
            <button
              type="button"
              className="tbpm-btn-virtual"
              onClick={handleVirtualSimulation}
              disabled={loading || resolving || Boolean(error)}
              title="Test thermal barcode dispatch virtually without physical hardware"
            >
              ⚡ Simulate Virtual Print
            </button>

            {/* Direct Hardware Dispatch (Uses Local Agent) */}
            <button
              type="button"
              className="tbpm-btn-print"
              onClick={handlePrint}
              disabled={loading || resolving || Boolean(error)}
              title="Dispatch directly to Thermal Printer via Local Print Agent"
            >
              {loading ? '⏳ Dispatching...' : '🖨️ Direct Thermal Print'}
            </button>

            {/* Fallback Browser Print (Explicitly available if technician needs paper sheet) */}
            <button
              type="button"
              className="tbpm-btn-browser-fallback"
              onClick={handleBrowserPrint}
              disabled={loading || resolving || Boolean(error)}
              title="Open browser print dialog for 50x25mm vector label"
            >
              📄 Browser Print
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThermalBarcodePrintModal;
